import { type InteractionType, type ProspectDto, useSalesGetInteractions } from "@crowdcoursing/api/openapi";
import { Message, Sort } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";

import { InteractionCard } from "~ui/molecules/InteractionCard/InteractionCard";
import { InteractionForm } from "~ui/molecules/InteractionForm/InteractionForm";
import { NoteCard } from "~ui/molecules/NoteCard/NoteCard";
import { NoteForm } from "~ui/molecules/NoteForm/NoteForm";
import { StageHistoryTimeline } from "~ui/molecules/StageHistoryTimeline/StageHistoryTimeline";
import { INTERACTION_TYPES } from "~utils/constants/interactionTypes";

import { useNoteSort } from "./useNoteSort";

const tInteractions = "Interactions";
const tLog = "Log";
const tSave = "Save";
const tCancel = "Cancel";
const tNoInteractions = "No interactions logged yet. Use the buttons above to log calls, emails, and meetings.";
const tNotes = "Notes";
const tSortOldest = "Sort by oldest first";
const tSortNewest = "Sort by newest first";
const tAddNote = "Add Note";
const tNoNotes = "No notes yet. Add notes to keep track of important information.";
const tStageHistory = "Stage History";
const tLoadingInteractions = "Loading interactions";

interface ActivityTabProps {
  prospect: ProspectDto;
  noteModal: {
    isAddOpen: boolean;
    editNote: NonNullable<ProspectDto["notes"]>[number] | null;
    noteToDelete: NonNullable<ProspectDto["notes"]>[number] | null;
    deleteConfirmOpen: boolean;
    openAddModal: () => void;
    closeAddModal: () => void;
    openEditModal: (note: NonNullable<ProspectDto["notes"]>[number]) => void;
    closeEditModal: () => void;
    openDeleteModal: (note: NonNullable<ProspectDto["notes"]>[number]) => void;
    closeDeleteModal: () => void;
    handleAddNote: (values: { body: string; pinned: boolean }) => Promise<void>;
    handleUpdateNote: (values: { body: string; pinned: boolean }) => Promise<void>;
    handleDeleteNote: () => Promise<void>;
    handlePinToggle: (note: NonNullable<ProspectDto["notes"]>[number]) => Promise<void>;
  };
  interactionModal: {
    isAddOpen: boolean;
    editInteraction: NonNullable<ProspectDto["interactions"]>[number] | null;
    interactionType: InteractionType;
    openAddModal: (type?: InteractionType) => void;
    closeAddModal: () => void;
    openEditModal: (interaction: NonNullable<ProspectDto["interactions"]>[number]) => void;
    closeEditModal: () => void;
    handleAddInteraction: (values: {
      type: InteractionType;
      subject: string;
      notes: string;
      contactId: string | null;
      occurredAt: string;
    }) => Promise<void>;
    handleUpdateInteraction: (values: {
      type: InteractionType;
      subject: string;
      notes: string;
      contactId: string | null;
      occurredAt: string;
    }) => Promise<void>;
    handleDeleteInteraction: (interactionId: string) => Promise<void>;
  };
}

export const ActivityTab = ({ prospect, noteModal, interactionModal }: ActivityTabProps) => {
  const { sortNewestFirst, setSortNewestFirst, pinnedNotes, regularNotes } = useNoteSort(prospect.notes ?? []);

  // Fetch interactions separately
  const { data: interactionsData, isLoading: isLoadingInteractions } = useSalesGetInteractions({
    pathParams: { id: prospect.id ?? "" },
  });
  const interactions = interactionsData ?? [];

  return (
    <Box display="grid" gap={3}>
      {/* Interactions */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{tInteractions}</Typography>
          <Stack direction="row" spacing={1}>
            {INTERACTION_TYPES.filter((type) => type.value !== 99).map((type) => (
              <Tooltip key={type.value} title={`${tLog} ${type.label}`}>
                <IconButton
                  size="small"
                  onClick={() => interactionModal.openAddModal(type.value)}
                  sx={{ color: type.color }}
                >
                  {type.icon}
                </IconButton>
              </Tooltip>
            ))}
          </Stack>

          {/* Add Interaction Dialog */}
          <Dialog open={interactionModal.isAddOpen} onClose={interactionModal.closeAddModal} maxWidth="sm" fullWidth>
            <DialogTitle>
              {tLog} {INTERACTION_TYPES.find((t) => t.value === interactionModal.interactionType)?.label}
            </DialogTitle>
            <DialogContent>
              <Box pt={1}>
                <InteractionForm
                  contacts={prospect.contacts ?? undefined}
                  initialValues={{ type: interactionModal.interactionType }}
                  onSubmit={interactionModal.handleAddInteraction}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={interactionModal.closeAddModal}>{tCancel}</Button>
              <Button type="submit" form="interaction-form" variant="contained">
                {tSave}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
        <Box display="grid" gap={2}>
          {isLoadingInteractions ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress aria-label={tLoadingInteractions} />
            </Box>
          ) : interactions.length > 0 ? (
            interactions
              .sort(
                (a, b) =>
                  DateTime.fromISO(b.occurredAt ?? "").toMillis() - DateTime.fromISO(a.occurredAt ?? "").toMillis(),
              )
              .map((interaction) => (
                <InteractionCard
                  key={interaction.id}
                  interaction={interaction}
                  prospect={prospect}
                  interactionModal={interactionModal}
                />
              ))
          ) : (
            <Typography color="text.secondary">{tNoInteractions}</Typography>
          )}
        </Box>
      </Paper>

      {/* Stage History and Notes */}
      <Box
        display="grid"
        gap={3}
        sx={{
          gridTemplateColumns: {
            md: "2fr 1fr", // 2/3 and 1/3 split on medium screens and up
            xs: "1fr", // Single column on small screens
          },
        }}
      >
        {/* Notes */}
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{tNotes}</Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title={sortNewestFirst ? tSortOldest : tSortNewest}>
                <IconButton
                  size="small"
                  color={sortNewestFirst ? "primary" : "default"}
                  onClick={() => setSortNewestFirst(!sortNewestFirst)}
                  aria-label={sortNewestFirst ? tSortOldest : tSortNewest}
                >
                  <Sort />
                </IconButton>
              </Tooltip>
              <Button variant="outlined" size="small" startIcon={<Message />} onClick={noteModal.openAddModal}>
                {tAddNote}
              </Button>
            </Stack>
          </Box>

          <Box display="grid" gap={2}>
            {/* Pinned Notes */}
            {pinnedNotes.length > 0 && (
              <>
                <Box
                  display="grid"
                  gap={2}
                  sx={{
                    gridTemplateColumns: {
                      md: "1fr 1fr",
                      xs: "1fr",
                    },
                  }}
                >
                  {pinnedNotes.map((note) => (
                    <NoteCard key={note.id} note={note} noteModal={noteModal} />
                  ))}
                </Box>
                <Divider />
              </>
            )}

            {/* Regular Notes */}
            {regularNotes.length > 0 ? (
              <Box
                display="grid"
                gap={2}
                sx={{
                  gridTemplateColumns: {
                    md: "1fr 1fr",
                    xs: "1fr",
                  },
                }}
              >
                {regularNotes.map((note) => (
                  <NoteCard key={note.id} note={note} noteModal={noteModal} />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary">{tNoNotes}</Typography>
            )}
          </Box>

          {/* Add Note Dialog */}
          <Dialog open={noteModal.isAddOpen} onClose={noteModal.closeAddModal} maxWidth="sm" fullWidth>
            <DialogTitle>{tAddNote}</DialogTitle>
            <DialogContent>
              <Box pt={1}>
                <NoteForm onSubmit={noteModal.handleAddNote} />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={noteModal.closeAddModal}>{tCancel}</Button>
              <Button type="submit" form="note-form" variant="contained">
                {tAddNote}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>

        {/* Stage History */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {tStageHistory}
          </Typography>
          <StageHistoryTimeline prospect={prospect} />
        </Paper>
      </Box>
    </Box>
  );
};
